import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtEur, fmtPct } from "@/lib/format";
import { CheckCircle2 } from "lucide-react";
import { loadSim, UNLOCK_KEY } from "@/lib/api";
import { TriCompare, triHypotheses } from "@/components/TriResult";
import { UnlockForm } from "@/components/UnlockForm";

function keyFigure(slug, r) {
  if (slug === "louer-vendre") {
    const opt = r.recommandation === "louer" ? "continuer à louer" : "vendre";
    return { value: fmtEur(r.difference), caption: `de plus sur ${r.horizon} ans en choisissant de ${opt}` };
  }
  if (slug === "tri-rendement") {
    return r.tri === null
      ? { value: fmtPct(r.rendementNet, 2), caption: "de rendement net initial (indiquez un apport pour le TRI)" }
      : { value: fmtPct(r.tri, 2), caption: `de TRI net sur ${r.horizon} ans, après impôt sur la plus-value` };
  }
  if (slug === "cash-flow") {
    const net = r.cashflowNetAvecVacance;
    return net >= 0
      ? { value: `+${fmtEur(net)}`, unit: "/mois", caption: "de cash-flow net positif (vacance incluse)" }
      : { value: fmtEur(Math.abs(net)), unit: "/mois", caption: "d'effort d'épargne mensuel (vacance incluse)" };
  }
  const opt = r.recommandation === "colocation" ? "la colocation" : "la location simple";
  return { value: fmtEur(r.difference), unit: "/an", caption: `de revenu net en plus avec ${opt}` };
}

function Bar({ label, value, display, max, winner }) {
  const pct = max > 0 ? Math.max(6, (value / max) * 100) : 6;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-sm font-semibold ${winner ? "text-[#C84C2C]" : "text-[#8C7A70]"}`}>
          {label}
        </span>
        <span className={`text-sm font-bold tabular-nums ${winner ? "text-[#231C18]" : "text-[#8C7A70]"}`}>
          {display}
        </span>
      </div>
      <div className="h-3 w-full bg-[#F5EFE6] rounded-full overflow-hidden">
        <div
          className={`animate-bar h-full rounded-full ${winner ? "bg-[#C84C2C]" : "bg-[#D8CCBF]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Indicator({ label, value }) {
  return (
    <div className="bg-[#FFFFFF] border border-[#E6DDD4] rounded-2xl p-4">
      <p className="text-[11px] font-semibold tracking-wide uppercase text-[#8C7A70] leading-tight">
        {label}
      </p>
      <p className="mt-2 text-lg font-bold text-[#231C18] tabular-nums">{value}</p>
    </div>
  );
}

function CashCard({ label, value, hint }) {
  const positive = value >= 0;
  return (
    <div className="bg-[#FFFFFF] border border-[#E6DDD4] rounded-2xl p-5" data-testid={`cash-card-${label}`}>
      <p className="text-[11px] font-semibold tracking-wide uppercase text-[#8C7A70]">{label}</p>
      <p className={`mt-2 text-3xl font-bold tabular-nums ${positive ? "text-[#2E7D32]" : "text-[#C62828]"}`}>
        {fmtEur(value)}
        <span className="text-base font-semibold text-[#8C7A70]"> /mois</span>
      </p>
      <p className="mt-1 text-xs text-[#8C7A70]">{hint}</p>
    </div>
  );
}

export default function Result() {
  const navigate = useNavigate();
  const hypRef = useRef(null);
  const sim = useMemo(() => loadSim(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [avecVacance, setAvecVacance] = useState(true);
  const [sentTo, setSentTo] = useState(() => sessionStorage.getItem(UNLOCK_KEY));
  const unlocked = !!sentTo;

  if (!sim || !sim.resultat) {
    return (
      <div className="p-6 flex flex-col min-h-screen justify-center">
        <p className="text-[#231C18]">Aucune simulation trouvée.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 h-[54px] bg-[#C84C2C] text-white rounded-full font-semibold"
        >
          Nouvelle simulation
        </button>
      </div>
    );
  }

  const { slug, resultat: r } = sim;
  const isLV = slug === "louer-vendre";
  const isCF = slug === "cash-flow";
  const isTRI = slug === "tri-rendement";

  let resume, winnerLabelKey;
  if (isLV) {
    const opt = r.recommandation === "louer" ? "continuer à louer" : "vendre";
    resume = `Sur ${r.horizon} ans, l'option « ${opt} » rapporterait ${fmtEur(r.difference)} de plus.`;
    winnerLabelKey = r.recommandation;
  } else if (isTRI) {
    resume =
      r.tri === null
        ? `Rendement net initial de ${fmtPct(r.rendementNet, 2)}. Indiquez un apport pour calculer le TRI.`
        : `Sur ${r.horizon} ans, votre TRI atteint ${fmtPct(r.tri, 2)} contre un rendement net initial de ${fmtPct(r.rendementNet, 2)}.`;
  } else if (isCF) {
    const net = r.cashflowNetAvecVacance;
    resume =
      net >= 0
        ? `Votre investissement dégage un cash-flow net positif de ${fmtEur(net)} par mois (vacance incluse).`
        : `Votre investissement nécessite un effort d'épargne de ${fmtEur(Math.abs(net))} par mois (vacance incluse).`;
  } else {
    const opt = r.recommandation === "colocation" ? "colocation" : "location simple";
    resume = `L'option « ${opt} » génère ${fmtEur(r.difference)} de revenu net en plus par an.`;
    winnerLabelKey = r.recommandation;
  }

  const prepareLead = () => {
    sessionStorage.setItem("assertimmo_resume", resume);
    sessionStorage.setItem("assertimmo_hypotheses", hypRef.current?.innerText || "");
  };

  const goLead = (mode) => {
    prepareLead();
    navigate(`/contact?mode=${mode}`);
  };

  const onUnlocked = (email) => {
    sessionStorage.setItem(UNLOCK_KEY, email);
    setSentTo(email);
  };

  const kf = keyFigure(slug, r);

  const barMax = isLV
    ? Math.max(r.patrimoineLouer, r.patrimoineVendre)
    : Math.max(r.netColoc, r.netSimple);

  const triWinnerLouer = isLV && r.triLouer >= r.triVendre;

  return (
    <div className="flex flex-col min-h-screen px-6 pt-8 pb-12 space-y-6" data-testid="result-screen">
      {/* Recommandation */}
      <div className="bg-[#1C1613] text-[#FFFFFF] rounded-2xl p-6 shadow-lg animate-fade-up">
        <span className="inline-block text-[11px] font-semibold tracking-[0.15em] uppercase text-[#E9B65A] mb-3">
          Recommandation
        </span>
        <h1 className="font-serif text-3xl font-semibold leading-tight" data-testid="result-titre-reco">
          {r.titreReco}
        </h1>
        <p className="mt-5 font-serif text-4xl font-bold tabular-nums text-[#E9B65A] leading-none" data-testid="result-key-figure">
          {kf.value}
          {kf.unit && <span className="text-lg font-semibold text-[#E6DDD4]"> {kf.unit}</span>}
        </p>
        <p className="mt-2 text-sm text-[#E6DDD4] leading-relaxed" data-testid="result-key-caption">{kf.caption}</p>
      </div>

      {unlocked ? (
        <div className="flex items-start gap-3 bg-[#2E7D32]/10 text-[#1B5E20] rounded-2xl p-4 animate-fade-up" data-testid="result-sent-msg">
          <CheckCircle2 strokeWidth={1.75} className="h-5 w-5 shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed">
            Analyse envoyée à <span className="font-semibold">{sentTo}</span>. Pensez à vérifier vos courriers indésirables.
          </p>
        </div>
      ) : (
        <UnlockForm sim={sim} prepare={prepareLead} onUnlocked={onUnlocked} />
      )}

      <div
        data-testid="result-detail"
        data-locked={unlocked ? "false" : "true"}
        aria-hidden={!unlocked}
        className={`space-y-6 transition-[filter] duration-500 ${unlocked ? "" : "blur-[7px] select-none pointer-events-none opacity-80"}`}
      >

      {/* Cash-flow (toggle vacance + 2 cash-flows) */}
      {isCF && (
        <>
          <div className="bg-[#FFFFFF] border border-[#E6DDD4] rounded-2xl p-4 flex items-center justify-between gap-3 animate-fade-up">
            <span className="text-sm font-semibold text-[#231C18]">
              Prendre en compte la vacance locative
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={avecVacance}
              data-testid="result-vacance-toggle"
              onClick={() => setAvecVacance((x) => !x)}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${avecVacance ? "bg-[#C84C2C]" : "bg-[#E6DDD4]"}`}
            >
              <span className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${avecVacance ? "translate-x-5" : ""}`} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 animate-fade-up">
            <CashCard
              label="Cash-flow brut"
              value={avecVacance ? r.cashflowBrutAvecVacance : r.cashflowBrutSansVacance}
              hint={avecVacance ? "Loyers hors charges − vacance − crédit" : "Loyers hors charges − crédit"}
            />
            <CashCard
              label="Cash-flow net"
              value={avecVacance ? r.cashflowNetAvecVacance : r.cashflowNetSansVacance}
              hint="Après toutes les charges déduites"
            />
          </div>
        </>
      )}

      {isTRI && <TriCompare r={r} />}

      {/* Barres comparatives */}
      {!isCF && !isTRI && (
      <div className="bg-[#FFFFFF] border border-[#E6DDD4] rounded-2xl p-5 space-y-5 animate-fade-up">
        {isLV ? (
          <>
            <Bar label="Continuer à louer" value={r.patrimoineLouer} display={fmtEur(r.patrimoineLouer)} max={barMax} winner={winnerLabelKey === "louer"} />
            <Bar label="Vendre" value={r.patrimoineVendre} display={fmtEur(r.patrimoineVendre)} max={barMax} winner={winnerLabelKey === "vendre"} />
            <p className="text-xs text-[#8C7A70]">Patrimoine estimé à {r.horizon} ans.</p>
          </>
        ) : (
          <>
            <Bar label="Colocation" value={r.netColoc} display={fmtEur(r.netColoc)} max={barMax} winner={winnerLabelKey === "colocation"} />
            <Bar label="Location simple" value={r.netSimple} display={fmtEur(r.netSimple)} max={barMax} winner={winnerLabelKey === "location-simple"} />
            <p className="text-xs text-[#8C7A70]">Revenu net annuel.</p>
          </>
        )}
      </div>
      )}

      {/* TRI (louer-vendre uniquement) */}
      {isLV && (
        <div className="bg-[#1C1613] text-[#FFFFFF] rounded-2xl p-6 animate-fade-up">
          <span className="inline-block text-[11px] font-semibold tracking-[0.15em] uppercase text-[#E9B65A] mb-4">
            Taux de rendement interne
          </span>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${triWinnerLouer ? "text-white font-semibold" : "text-[#D8CCBF]"}`}>
                TRI location (conserver)
              </span>
              <span className={`text-lg font-bold tabular-nums ${triWinnerLouer ? "text-white" : "text-[#D8CCBF]"}`}>
                {fmtPct(r.triLouer, 2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${!triWinnerLouer ? "text-white font-semibold" : "text-[#D8CCBF]"}`}>
                TRI vente + placement
              </span>
              <span className={`text-lg font-bold tabular-nums ${!triWinnerLouer ? "text-white" : "text-[#D8CCBF]"}`}>
                {fmtPct(r.triVendre, 2)}
              </span>
            </div>
          </div>
          <p className="mt-4 text-xs text-[#E6DDD4]/80 leading-relaxed">
            TRI comparables, nets d'impôts (TMI côté location, flat tax {String(r.hypotheses.fiscalitePlacement).replace(".", ",")} % côté placement).{" "}
            {triWinnerLouer ? "Conserver et louer" : "Vendre puis placer"} offre le meilleur rendement.
          </p>
        </div>
      )}

      {/* Indicateurs clés */}
      <div className="grid grid-cols-2 gap-3 animate-fade-up">
        {isTRI ? (
          <>
            <Indicator label="Mensualité de crédit" value={fmtEur(r.mensualite)} />
            <Indicator label="Cash-flow mensuel (an 1)" value={fmtEur(r.cashflowMensuelAn1)} />
            <Indicator label="Prix de revente estimé" value={fmtEur(r.prixRevente)} />
            <Indicator label="Impôt sur la plus-value" value={fmtEur(r.plusValue.impotTotal)} />
            <Indicator label="Net à la revente" value={fmtEur(r.netRevente)} />
            <Indicator label="Gain net total" value={fmtEur(r.gainTotal)} />
          </>
        ) : isCF ? (
          <>
            <Indicator label="Revenus mensuels" value={fmtEur(avecVacance ? r.revenusMensuelsAvecVacance : r.revenusMensuelsSansVacance)} />
            <Indicator label="Coût vacance / mois" value={fmtEur(r.coutVacanceMensuel)} />
            <Indicator label="Mensualité de crédit" value={fmtEur(r.detailDepenses.emprunt)} />
            <Indicator label="Gestion locative / mois" value={fmtEur(avecVacance ? r.detailDepenses.gestionAvecVacance : r.detailDepenses.gestionSansVacance)} />
          </>
        ) : isLV ? (
          <>
            <Indicator label="Écart de patrimoine" value={fmtEur(r.difference)} />
            <Indicator label="Rendement brut" value={fmtPct(r.rendementBrut, 2)} />
            <Indicator label="Cash-flow mensuel net" value={fmtEur(r.cashflowMensuelNet)} />
            <Indicator label="Net de vente immédiat" value={fmtEur(r.netVenteImmediat)} />
            <Indicator label="Impôt plus-value (maintenant)" value={fmtEur(r.impotPlusValueNow)} />
            <Indicator label={`Impôt plus-value (à ${r.horizon} ans)`} value={fmtEur(r.impotPlusValueFin)} />
          </>
        ) : (
          <>
            <Indicator label="Écart annuel" value={fmtEur(r.difference)} />
            <Indicator label="Gain mensuel" value={fmtEur(r.gainMensuelSupplementaire)} />
            <Indicator label="Loyers bruts colocation" value={fmtEur(r.brutColoc)} />
            <Indicator label="Loyers bruts loc. simple" value={fmtEur(r.brutSimple)} />
          </>
        )}
      </div>

      {/* Hypothèses */}
      <div className="bg-[#F5EFE6] rounded-2xl p-5 animate-fade-up">
        <p className="text-xs font-semibold tracking-wide uppercase text-[#8C7A70] mb-2">
          Hypothèses
        </p>
        <p ref={hypRef} data-testid="result-hypotheses-text" className="text-xs text-[#231C18]/80 leading-relaxed">
          {isTRI ? (
            triHypotheses(r)
          ) : isCF ? (
            <>
              Cash-flow mensuel. Les charges non mensuelles (copropriété, taxe foncière, travaux, autres) sont ramenées au mois. La gestion locative ({String(r.hypotheses.gestionLocative).replace(".", ",")} %) s'applique sur les revenus encaissés. La vacance locative ({String(r.joursVacance).replace(".", ",")} j/an, soit {String(r.tauxVacance).replace(".", ",")} %) réduit les loyers et les charges encaissées. Estimations indicatives, hors fiscalité.
            </>
          ) : isLV ? (
            <>
              Location : loyers nets de charges, gestion ({String(r.hypotheses.fraisGestion).replace(".", ",")} %) et impôt sur le revenu (TMI), cumulés sans placement. Vente : capital placé à {String(r.hypotheses.tauxPlacement).replace(".", ",")} % brut, soit {String(r.hypotheses.tauxPlacementNet).replace(".", ",")} % net après flat tax {String(r.hypotheses.fiscalitePlacement).replace(".", ",")} %. Frais de revente {String(r.hypotheses.fraisVente).replace(".", ",")} %, impôt sur la plus-value inclus (abattements pour durée de détention).
            </>
          ) : (
            <>
              Location simple : loyer charges comprises, net des charges réelles et des honoraires de gestion ({String(r.hypotheses.honorairesSimple).replace(".", ",")} %). Colocation : loyers par chambre charges comprises, net des charges et des honoraires ({String(r.hypotheses.honorairesColoc).replace(".", ",")} %). Revenus nets annuels, hors fiscalité. Estimations indicatives.
            </>
          )}
        </p>
      </div>
      </div>

      {/* CTA */}
      <div className="bg-[#FFFFFF] border border-[#E6DDD4] rounded-2xl p-5 space-y-3 animate-fade-up">
        <h3 className="font-serif text-xl font-semibold text-[#231C18]">
          Aller plus loin
        </h3>
        <button
          data-testid="result-cta-expert"
          onClick={() => goLead("expert")}
          className="h-[54px] w-full bg-transparent border-2 border-[#C84C2C] text-[#C84C2C] rounded-full font-semibold transition-transform active:scale-[0.98] hover:bg-[#C84C2C]/5"
        >
          Être recontacté par un expert
        </button>
        <button
          data-testid="result-cta-nouvelle"
          onClick={() => navigate("/")}
          className="h-[54px] w-full bg-[#F5EFE6] text-[#231C18] rounded-full font-semibold transition-transform active:scale-[0.98] hover:bg-[#E6DDD4]"
        >
          Nouvelle simulation
        </button>
      </div>
    </div>
  );
}
